export type RequestProps = {
    target?: string;
    hostPort?: string;
    host?: string;
    port?: string;
    username?: string;
    database?: string;
    password?: string;
    protocol?: string;
    service?: string;
};

export default class DsnService {
    public static getDsn({
        target = 'example',
        hostPort = '{your_host:your_port}',
        host = '{your_host}',
        port = '{your_port}',
        username = '{your_username}',
        database = '{your_database_name}',
        password = '{your_password}',
        protocol = 'tcp',
        service = 'mysql',
    }: RequestProps): string {
        let dsn = '';

        switch (target) {
            case 'dsn.redis':
                dsn = `${service}://${username}:${password}@${hostPort}/0`;
                break;
            case 'dsn':
                if (service === 'rediss') {
                    dsn = `${service}://${username}:${password}@${hostPort}`;
                } else if (service === 'mssql') {
                    dsn = `Data Source=${hostPort},${port};User ID=${username};Password=${password}`;
                } else if (service === 'postgres') {
                    dsn = `${service}://${username}:${password}@${hostPort}:${port}/${database}`;
                } else {
                    dsn = `${service}://${username}:${password}@${hostPort}/${database}`;
                }

                break;
            case 'go':
                dsn = `${username}:${password}@${hostPort})/${database}`;
                break;
            case 'php.pdo':
                dsn = `${service}://${username}:${password}@${hostPort}/${database}`;
                break;
            case 'php.pear.db':
                dsn = `${service}://${username}:${password}@${hostPort}/${database}`;
                break;
            case 'example':
            default:
                dsn = `mysql://{your_username}:{your_password}@tcp(database_host:database_port)/{database_name}`;
        }

        return dsn;
    }

    public static getDsnUsageExample({
        target = 'example',
        hostPort = '{your_host:your_port}',
        host = '{your_host}',
        port = '{your_port}',
        username = '{your_username}',
        database = '{your_database_name}',
        password = '{your_password}',
        protocol = 'tcp',
        service = 'mysql',
    }: RequestProps): string[] {
        let dsn: string[] = [];

        switch (target) {
            case 'ado.net':
                dsn = [
                    `Server=${host};`,
                    `Port=${port};`,
                    `Database=${database};`,
                    `Uid=${username};`,
                    `Pwd=${password};`,
                    `SslMode=Required;`,
                ];
                break;
            case 'jdbc':
                dsn = [
                    `String url = "jdbc:${service}://%s:%s/%s?verifyServerCertificate=true&useSSL=true&requireSSL=true", ${host}, ${port}, ${database}";`,
                    `myDbConn = DriverManager.getConnection(url, ${username}, ${password}";`,
                ];
                break;
            case 'node.js':
                dsn =
                    service === 'mysql'
                        ? [
                              `var conn = mysql.createConnection({host: ${host}, user: ${username}, password: ${password}, database: ${database}, Port: ${port}, ssl: {ca:fs.readFileSync({ca-cert filename})}});`,
                          ]
                        : [
                              `const pg = require('pg');`,
                              ``,
                              `var conn = pg.Client({host: ${host}, user: ${username}, password: ${password}, database: ${database}, port: ${port}, ssl: true);`,
                          ];
                break;
            case 'odbc':
                dsn =
                    service === 'mysql'
                        ? [
                              `DRIVER={MySQL ODBC 5.3 UNICODE Driver};`,
                              `Server=${host};`,
                              `Port=${port};`,
                              `Database=${database};`,
                              `Uid=${username};`,
                              `Pwd=${password};`,
                              `sslca={ca-cert filename};`,
                              `sslverify=1;`,
                              `Option=3;`,
                          ]
                        : [
                              `DRIVER={PostgreSQL};`,
                              `Server=${host};`,
                              `Port=${port};`,
                              `Database=${database};`,
                              `Uid=${username};`,
                              `Pwd=${password};`,
                              `sslca={ca-cert filename};`,
                              `sslverify=1;`,
                              `Option=3;`,
                          ];
                break;
            case 'php':
                dsn =
                    service === 'mysql'
                        ? [
                              `<?php`,
                              ``,
                              `$con = mysqli_init();`,
                              `mysqli_ssl_set($con, NULL, NULL, {ca-cert filename}, NULL, NULL);`,
                              `mysqli_real_connect($con, ${host}, ${username}, ${password}, ${database}, ${port});`,
                          ]
                        : [
                              `<?php`,
                              ``,
                              `$connection = pg_connect("host='${host}' dbname='${database}' user='${username}' password='${password}'")`,
                              `  or die("Failed to create connection to database: ". pg_last_error());`,
                          ];
                break;
            case 'php.pdo':
                const dsnPdo = this.getDsn({
                    target,
                    hostPort,
                    host,
                    port,
                    username,
                    database,
                    password,
                    protocol,
                    service,
                });
                dsn = [
                    `<?php`,
                    ``,
                    `$dsn = '${dsnPdo}`,
                    ``,
                    `$dbh = new PDO($dsn, $username, $password);`,
                ];
                break;
            case 'php.pear.db':
                const dsnPear = this.getDsn({
                    target,
                    hostPort,
                    host,
                    port,
                    username,
                    database,
                    password,
                    protocol,
                    service,
                });
                dsn = [
                    `<?php`,
                    ``,
                    `require_once('DB.php');`,
                    ``,
                    `$db = DB::connect('${dsnPear}');`,
                ];
                break;
            case 'python':
                dsn =
                    service === 'mysql'
                        ? [
                              `cnx = ${service}.connector.connect(user=${username}, password=${password}, host=${host}, port=${port}, database=${database}, ssl_ca={ca-cert filename}, ssl_verify_cert=true)`,
                          ]
                        : [
                              `import psycopg2`,
                              ``,
                              `host = "${host}:${port}"`,
                              `dbname = "${database}"`,
                              `user = "${username}"`,
                              `password = "${password}"`,
                              `sslmode = "require"`,
                              ``,
                              `conn_string = "host={0} user={1} dbname={2} password={3} sslmode={4}".format(host, user, dbname, password, sslmode)`,
                              `conn = psycopg2.connect(conn_string)`,
                          ];
                break;
            case 'ruby':
                dsn =
                    service === 'mysql'
                        ? [
                              `client = Mysql2::Client.new(username: ${username}, password: ${password}, database: ${database}, host: ${host}, port: ${port}, sslca:{ca-cert filename}, sslverify:false, sslcipher:'AES256-SHA')`,
                          ]
                        : [
                              `require 'pg'`,
                              ``,
                              `begin`,
                              `  connection = PG::Connection.new(:host => '${host}', :user => '${username}', :dbname => '${database}', :port => '${port}', :password => '${password}')`,
                          ];
                break;
            case 'go':
                const dsnGo = this.getDsn({
                    target,
                    hostPort,
                    host,
                    port,
                    username,
                    database,
                    password,
                    protocol,
                    service,
                });
                dsn =
                    service === 'mysql'
                        ? [
                              `import (`,
                              `  "database/sql"`,
                              `  _ "github.com/go-sql-driver/mysql"`,
                              `)`,
                              ``,
                              `db, err := sql.Open("${service}", "${dsnGo}")`,
                          ]
                        : [
                              `import (`,
                              `  "database/sql"`,
                              `  "fmt"`,
                              `  _ "github.com/lib/pq"`,
                              `)`,
                              ``,
                              `	var connectionString string = fmt.Sprintf("host=%s user=%s password=%s dbname=%s sslmode=require", "${host}", "${username}", "${password}", "${database}")`,
                              ``,
                              `db, err := sql.Open("postgres", "${dsnGo}")`,
                          ];
                break;
            case 'dsn':
                dsn = [
                    this.getDsn({
                        target,
                        hostPort,
                        host,
                        port,
                        username,
                        database,
                        password,
                        protocol,
                        service,
                    }),
                ];
                break;
        }

        return dsn;
    }
}
